'use client';

import { ArrowRight, Search, X } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export interface SearchResultItem {
  id: string;
  type: 'property' | 'post' | 'page';
  title: string;
  subtitle?: string;
  imageUrl?: string;
  href: string;
  meta?: string;
}

export interface StoneSearchOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  locale?: string;
  messages?: Record<string, unknown> | undefined;
  properties?: SearchResultItem[];
  posts?: SearchResultItem[];
  pages?: SearchResultItem[];
}

type CategoryKey = 'all' | 'properties' | 'journal' | 'pages';

const DEBOUNCE_MS = 250;
const MAX_RESULTS = 8;

const QUICK_LINKS: { key: string; fallbackLabel: string; href: (l: string) => string }[] = [
  { key: 'theCollection', fallbackLabel: 'The Collection', href: l => `/${l}/properties` },
  { key: 'theFirm', fallbackLabel: 'The Firm', href: l => `/${l}/about` },
  { key: 'valuations', fallbackLabel: 'Valuations', href: l => `/${l}/valuations` },
  { key: 'provence', fallbackLabel: 'Provence', href: l => `/${l}/properties?region=provence` },
  { key: 'mauritius', fallbackLabel: 'Mauritius', href: l => `/${l}/properties?region=mauritius` },
  { key: 'journal', fallbackLabel: 'Journal', href: l => `/${l}/news` },
  { key: 'contact', fallbackLabel: 'Contact', href: l => `/${l}/contact` },
];

function matchesQuery(text: string | undefined | null, query: string): boolean {
  if (!text) return false;
  return text.toLowerCase().includes(query);
}

export const StoneSearchOverlay = memo(function StoneSearchOverlay({
  isOpen,
  onClose,
  locale = 'en',
  messages,
  properties = [],
  posts = [],
  pages = [],
}: StoneSearchOverlayProps) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<CategoryKey>('all');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [hasInteracted, setHasInteracted] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<NodeJS.Timeout>(undefined);
  const resultsRef = useRef<HTMLDivElement>(null);

  const stoneMessages = (messages?.stone || {}) as Record<string, unknown>;
  const menuMessages = (stoneMessages.menu || {}) as Record<string, string>;
  const searchMessages = (stoneMessages.searchOverlay || {}) as Record<string, string>;

  const t = useCallback(
    (key: string, fallback: string): string => searchMessages[key] || fallback,
    [searchMessages],
  );

  const menuT = useCallback(
    (key: string, fallback: string): string => menuMessages[key] || fallback,
    [menuMessages],
  );

  // Debounce query
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQuery(query.trim().toLowerCase()), DEBOUNCE_MS);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  // Focus input and reset state when opened
  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setDebouncedQuery('');
    setActiveCategory('all');
    setSelectedIndex(-1);
    setHasInteracted(false);
    const timer = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(timer);
  }, [isOpen]);

  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Filter results
  const filteredProperties = useMemo(() => {
    if (!debouncedQuery) return [];
    return properties
      .filter(p => matchesQuery(p.title, debouncedQuery) || matchesQuery(p.subtitle, debouncedQuery) || matchesQuery(p.meta, debouncedQuery))
      .slice(0, MAX_RESULTS);
  }, [properties, debouncedQuery]);

  const filteredPosts = useMemo(() => {
    if (!debouncedQuery) return [];
    return posts
      .filter(p => matchesQuery(p.title, debouncedQuery) || matchesQuery(p.subtitle, debouncedQuery))
      .slice(0, MAX_RESULTS);
  }, [posts, debouncedQuery]);

  const filteredPages = useMemo(() => {
    if (!debouncedQuery) return [];
    return pages
      .filter(p => matchesQuery(p.title, debouncedQuery) || matchesQuery(p.subtitle, debouncedQuery))
      .slice(0, MAX_RESULTS);
  }, [pages, debouncedQuery]);

  const quickLinkResults = useMemo(() => {
    if (!debouncedQuery) return [];
    return QUICK_LINKS.filter(l =>
      menuT(l.key, l.fallbackLabel).toLowerCase().includes(debouncedQuery),
    ).map(l => ({
      id: `ql-${l.key}`,
      type: 'page' as const,
      title: menuT(l.key, l.fallbackLabel),
      href: l.href(locale),
    }));
  }, [debouncedQuery, locale, menuT]);

  const visibleResults = useMemo((): SearchResultItem[] => {
    if (!debouncedQuery) return [];
    switch (activeCategory) {
      case 'properties':
        return filteredProperties;
      case 'journal':
        return filteredPosts;
      case 'pages':
        return [...filteredPages, ...quickLinkResults];
      default:
        return [
          ...filteredProperties.slice(0, 3),
          ...filteredPosts.slice(0, 3),
          ...filteredPages.slice(0, 2),
          ...quickLinkResults.slice(0, 2),
        ].slice(0, MAX_RESULTS);
    }
  }, [activeCategory, debouncedQuery, filteredProperties, filteredPosts, filteredPages, quickLinkResults]);

  const totalCount = filteredProperties.length + filteredPosts.length + filteredPages.length + quickLinkResults.length;

  const categories: { key: CategoryKey; label: string; count: number }[] = useMemo(
    () => [
      { key: 'all', label: t('all', 'All'), count: totalCount },
      { key: 'properties', label: t('properties', 'Properties'), count: filteredProperties.length },
      { key: 'journal', label: t('journal', 'Journal'), count: filteredPosts.length },
      { key: 'pages', label: t('pages', 'Pages'), count: filteredPages.length + quickLinkResults.length },
    ],
    [t, totalCount, filteredProperties.length, filteredPosts.length, filteredPages.length, quickLinkResults.length],
  );

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, visibleResults.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, -1));
        if (selectedIndex <= 0) inputRef.current?.focus();
      } else if (e.key === 'Enter' && selectedIndex >= 0 && visibleResults[selectedIndex]) {
        e.preventDefault();
        window.location.href = visibleResults[selectedIndex].href;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, selectedIndex, visibleResults]);

  // Scroll selected into view
  useEffect(() => {
    if (selectedIndex >= 0 && resultsRef.current) {
      const el = resultsRef.current.children[selectedIndex] as HTMLElement | undefined;
      el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedIndex]);

  const handleInputChange = (value: string) => {
    setQuery(value);
    setSelectedIndex(-1);
    if (!hasInteracted) setHasInteracted(true);
  };

  const handleClear = () => {
    setQuery('');
    setDebouncedQuery('');
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  const isSearching = query.trim() !== '' && debouncedQuery !== query.trim().toLowerCase();
  const showNoResults = debouncedQuery && visibleResults.length === 0 && !isSearching;
  const showQuickLinks = !debouncedQuery && !hasInteracted;
  const showIdlePrompt = !debouncedQuery && hasInteracted;

  return (
    <div
      className={`fixed inset-0 z-[60] transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
        isOpen
          ? 'opacity-100 pointer-events-auto'
          : 'opacity-0 pointer-events-none'
      }`}
      role="dialog"
      aria-modal="true"
      aria-label={t('title', 'Search')}
    >
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-stone-900/[0.98] backdrop-blur-sm transition-opacity duration-700 ${
          isOpen ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Content */}
      <div
        className={`relative h-full w-full flex flex-col transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
          isOpen ? 'scale-100 opacity-100' : 'scale-[0.98] opacity-0'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 md:px-12 pt-8 pb-4">
          <button
            onClick={onClose}
            className="group flex items-center gap-2 text-stone-400 hover:text-white transition-colors duration-300"
            aria-label={t('close', 'Close search')}
          >
            <X
              size={22}
              strokeWidth={1.5}
              className="group-hover:rotate-90 transition-transform duration-300"
            />
            <span className="hidden md:inline text-xs uppercase tracking-[0.15em] font-sans">
              {t('close', 'Close')}
            </span>
          </button>

          <div className="hidden md:flex items-center gap-2 text-stone-600 text-xs font-sans tracking-wide">
            <kbd className="px-2 py-0.5 border border-stone-700 rounded text-[10px] text-stone-500">
              ESC
            </kbd>
            <span>{t('toClose', 'to close')}</span>
          </div>
        </div>

        {/* Search Area */}
        <div className="flex-1 overflow-y-auto px-6 md:px-12 lg:px-24 xl:px-48">
          {/* Title */}
          <div
            className={`text-center mb-8 md:mb-12 transition-all duration-700 delay-100 ${
              isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <h2
              className="font-serif text-2xl md:text-4xl text-stone-100 tracking-wide"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {t('title', 'Discover Your Sanctuary')}
            </h2>
          </div>

          {/* Input */}
          <div
            className={`max-w-2xl mx-auto mb-8 transition-all duration-700 delay-200 ${
              isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <div className="relative group">
              <div className="absolute left-0 top-1/2 -translate-y-1/2 pointer-events-none">
                {isSearching ? (
                  <div className="w-5 h-5 border-2 border-stone-500 border-t-stone-200 rounded-full animate-spin" />
                ) : (
                  <Search
                    size={20}
                    strokeWidth={1.5}
                    className="text-stone-500 group-focus-within:text-stone-300 transition-colors duration-300"
                  />
                )}
              </div>
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={e => handleInputChange(e.target.value)}
                placeholder={t('placeholder', 'Search properties, journal, pages...')}
                className="w-full bg-transparent border-0 border-b-2 border-stone-700 focus:border-stone-400 pl-8 pr-10 py-4 text-lg md:text-xl text-white placeholder:text-stone-600 font-sans outline-none transition-all duration-300 focus:shadow-[0_2px_20px_-4px_rgba(168,162,158,0.15)]"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
                autoComplete="off"
                spellCheck={false}
              />
              {/* Clear button */}
              <div
                className={`absolute right-0 top-1/2 -translate-y-1/2 transition-all duration-200 ${
                  query ? 'opacity-100 scale-100' : 'opacity-0 scale-75 pointer-events-none'
                }`}
              >
                <button
                  onClick={handleClear}
                  className="text-stone-500 hover:text-white transition-colors duration-200 p-1"
                  aria-label={t('clear', 'Clear search')}
                  tabIndex={query ? 0 : -1}
                >
                  <X size={18} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          </div>

          {/* Category Tabs */}
          {debouncedQuery && (
            <div
              className={`max-w-2xl mx-auto mb-8 transition-all duration-500 delay-100 ${
                debouncedQuery ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
              }`}
            >
              <div className="flex items-center gap-1 border-b border-stone-800">
                {categories.map(cat => (
                  <button
                    key={cat.key}
                    onClick={() => {
                      setActiveCategory(cat.key);
                      setSelectedIndex(-1);
                    }}
                    className={`relative px-4 py-2.5 text-xs uppercase tracking-[0.15em] font-sans transition-colors duration-300 ${
                      activeCategory === cat.key
                        ? 'text-white'
                        : 'text-stone-500 hover:text-stone-300'
                    }`}
                  >
                    <span>{cat.label}</span>
                    {cat.count > 0 && (
                      <span
                        className={`ml-1.5 text-[10px] transition-colors duration-300 ${
                          activeCategory === cat.key ? 'text-stone-300' : 'text-stone-600'
                        }`}
                      >
                        {cat.count}
                      </span>
                    )}
                    {/* Active indicator */}
                    {activeCategory === cat.key && (
                      <span className="absolute bottom-0 left-2 right-2 h-px bg-white transition-all duration-400" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Results */}
          {debouncedQuery && visibleResults.length > 0 && (
            <div ref={resultsRef} className="max-w-2xl mx-auto space-y-2 pb-8">
              {visibleResults.map((result, idx) => (
                <Link
                  key={result.id}
                  href={result.href}
                  onClick={onClose}
                  className={`group flex items-center gap-4 p-3 md:p-4 transition-all duration-300 hover:-translate-y-0.5 hover:bg-stone-800/50 ${
                    selectedIndex === idx ? 'bg-stone-800/60 -translate-y-0.5' : ''
                  }`}
                  style={{
                    animationDelay: `${idx * 80}ms`,
                    animation: 'searchResultIn 0.5s ease-out both',
                  }}
                  onMouseEnter={() => setSelectedIndex(idx)}
                >
                  {/* Thumbnail */}
                  {result.imageUrl ? (
                    <div className="w-16 h-16 md:w-20 md:h-20 shrink-0 overflow-hidden bg-stone-800 relative">
                      <Image
                        src={result.imageUrl}
                        alt={result.title}
                        fill
                        sizes="80px"
                        className="object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  ) : (
                    <div className="w-16 h-16 md:w-20 md:h-20 shrink-0 bg-stone-800 flex items-center justify-center">
                      <Search size={16} className="text-stone-600" strokeWidth={1.5} />
                    </div>
                  )}

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] uppercase tracking-[0.15em] font-sans text-stone-500">
                        {result.type === 'property'
                          ? t('properties', 'Property')
                          : result.type === 'post'
                            ? t('journal', 'Journal')
                            : t('pages', 'Page')}
                      </span>
                    </div>
                    <h3
                      className="text-sm md:text-base text-stone-100 font-serif truncate group-hover:text-white transition-colors duration-300"
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      {result.title}
                    </h3>
                    {result.subtitle && (
                      <p className="text-xs text-stone-500 truncate mt-0.5 font-sans">
                        {result.subtitle}
                      </p>
                    )}
                    {result.meta && (
                      <p className="text-[11px] text-stone-600 mt-0.5 font-sans">{result.meta}</p>
                    )}
                  </div>

                  {/* Arrow */}
                  <ArrowRight
                    size={16}
                    className="shrink-0 text-stone-700 group-hover:text-stone-300 group-hover:translate-x-1 transition-all duration-300"
                  />
                </Link>
              ))}

              {/* View all link */}
              {activeCategory === 'all' && totalCount > MAX_RESULTS && (
                <div className="pt-4 text-center">
                  <Link
                    href={`/${locale}/properties?q=${encodeURIComponent(query)}`}
                    onClick={onClose}
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-sans text-stone-400 hover:text-white transition-colors duration-300"
                  >
                    <span>
                      {t('viewAll', 'View all')} {totalCount} {t('results', 'results')}
                    </span>
                    <ArrowRight size={14} strokeWidth={1.5} />
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* No results */}
          {showNoResults && (
            <div className="max-w-2xl mx-auto text-center py-16 animate-pulse-slow">
              <Search size={32} strokeWidth={1} className="mx-auto text-stone-700 mb-4" />
              <p
                className="font-serif text-xl text-stone-400"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {t('noResults', 'No results found')}
              </p>
              <p className="text-xs text-stone-600 mt-2 font-sans">
                {t('tryDifferent', 'Try a different search term')}
              </p>
            </div>
          )}

          {/* Quick links (initial state) */}
          {showQuickLinks && (
            <div className="max-w-2xl mx-auto pt-4">
              <p className="text-[10px] uppercase tracking-[0.2em] font-sans text-stone-600 mb-6">
                {t('quickLinks', 'Quick Links')}
              </p>
              <div className="space-y-1">
                {QUICK_LINKS.map((link, idx) => (
                  <Link
                    key={link.key}
                    href={link.href(locale)}
                    onClick={onClose}
                    className="group flex items-center justify-between py-3 border-b border-stone-800/50 hover:border-stone-700 transition-all duration-300"
                    style={{
                      animationDelay: `${idx * 60 + 300}ms`,
                      animation: 'searchResultIn 0.5s ease-out both',
                    }}
                  >
                    <span
                      className="font-serif text-lg text-stone-300 group-hover:text-white group-hover:translate-x-2 transition-all duration-300"
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      {menuT(link.key, link.fallbackLabel)}
                    </span>
                    <ArrowRight
                      size={16}
                      className="text-stone-700 group-hover:text-stone-400 group-hover:translate-x-1 transition-all duration-300"
                    />
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Idle prompt (user cleared input) */}
          {showIdlePrompt && (
            <div className="max-w-2xl mx-auto text-center py-12">
              <p className="text-sm text-stone-600 font-sans">
                {t('startTyping', 'Start typing to search...')}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className={`px-6 md:px-12 py-4 border-t border-stone-800/50 flex items-center justify-between transition-all duration-700 delay-300 ${
            isOpen ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="hidden md:flex items-center gap-4 text-[10px] text-stone-600 font-sans tracking-wide">
            <span className="flex items-center gap-1.5">
              <kbd className="px-1.5 py-0.5 border border-stone-700 rounded text-[10px]">&uarr;</kbd>
              <kbd className="px-1.5 py-0.5 border border-stone-700 rounded text-[10px]">&darr;</kbd>
              {t('navigate', 'navigate')}
            </span>
            <span className="flex items-center gap-1.5">
              <kbd className="px-1.5 py-0.5 border border-stone-700 rounded text-[10px]">&crarr;</kbd>
              {t('select', 'select')}
            </span>
          </div>
          <p className="text-[10px] text-stone-700 font-sans tracking-wider uppercase">
            Stone Investment
          </p>
        </div>
      </div>

      {/* Keyframe animation injected via style tag */}
      <style jsx global>{`
        @keyframes searchResultIn {
          from {
            opacity: 0;
            transform: translateY(12px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-pulse-slow {
          animation: pulseSlow 2s ease-in-out infinite;
        }
        @keyframes pulseSlow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
      `}</style>
    </div>
  );
});

export default StoneSearchOverlay;
